/**
 * The job board.
 *
 * A contract is: a barge waiting somewhere, a berth it needs to reach, and a
 * payout that erodes with time and with rough handling. Delivery means parking
 * the barge inside the berth circle, roughly on heading, slow enough — then
 * letting go. The moment of release is the scoring moment, which is what makes
 * the last hundred metres the whole game.
 */

import { Vector3 } from 'three'
import type { Barge } from '../entities/Barge.ts'
import { BERTHS, BARGE_SPAWNS, type Berth } from '../world/Harbour.ts'

export type ContractPhase = 'offered' | 'towing' | 'delivered'

export interface Contract {
  id: number
  description: string
  berth: Berth
  /** Base payout before deductions. */
  fee: number
  /** Seconds on the clock when accepted. */
  timeAllowed: number
  timeUsed: number
  phase: ContractPhase
}

// Delivery tolerances — kept forgiving so a young player only has to nudge the
// barge into the berth roughly the right way, not thread it in dead slow and
// perfectly square. Get it in the circle, moving gently, pointing about right.
const MAX_SPEED = 3.5
const MAX_YAW_RATE = 0.25
const HEADING_TOLERANCE = 0.9

const CARGOES = [
  'machine parts',
  'grain',
  'containers',
  'quarry stone',
  'bunker fuel drums',
  'timber',
  'reefer boxes',
]

const _delta = new Vector3()

export class ContractBoard {
  current: Contract | null = null
  completed = 0
  earnings = 0
  /** Set by `update` in the frame a delivery lands; the UI shows and clears it. */
  lastResult: string | null = null

  private nextId = 1

  /** Draw the next job. Deterministic sequence, so a session is replayable. */
  offer(): Contract {
    const berth = BERTHS[(this.nextId * 7) % BERTHS.length]
    const spawn = BARGE_SPAWNS[(this.nextId * 5) % BARGE_SPAWNS.length]
    const distance = spawn.position.distanceTo(berth.position)

    const contract: Contract = {
      id: this.nextId++,
      description: `Tow the ${CARGOES[this.nextId % CARGOES.length]} barge to ${berth.label}`,
      berth,
      fee: Math.round(2200 + distance * 6),
      // Generous: the distance at 2.5 m/s, plus ten minutes for the tricky bit.
      timeAllowed: Math.round(distance / 2.5 + 600),
      timeUsed: 0,
      phase: 'offered',
    }
    this.current = contract
    return contract
  }

  /** Spawn parameters for the current contract's barge. */
  spawnFor(contract: Contract): { position: Vector3; heading: number } {
    return BARGE_SPAWNS[(contract.id * 5) % BARGE_SPAWNS.length]
  }

  markTowing(): void {
    if (this.current?.phase === 'offered') this.current.phase = 'towing'
  }

  /**
   * Check the delivery conditions. Call when the player releases the hawser;
   * returns true when the drop counts.
   */
  tryDeliver(barge: Barge): boolean {
    const contract = this.current
    if (!contract || contract.phase !== 'towing') return false

    _delta.copy(barge.position).sub(contract.berth.position)
    _delta.y = 0
    if (_delta.length() > contract.berth.radius) return false
    if (barge.speed > MAX_SPEED) return false
    if (Math.abs(barge.yawRate) > MAX_YAW_RATE) return false

    // Heading, mod pi — a barge berthed stern-first is berthed all the same.
    let headingError = Math.abs(barge.yaw - contract.berth.heading) % Math.PI
    if (headingError > Math.PI / 2) headingError = Math.PI - headingError
    if (headingError > HEADING_TOLERANCE) return false

    // Score: time pressure and hull damage both come off the top.
    const timeFactor = Math.max(0.35, 1 - Math.max(0, contract.timeUsed - contract.timeAllowed) / contract.timeAllowed)
    const damageFactor = 1 - barge.damage * 0.6
    const payout = Math.round(contract.fee * timeFactor * damageFactor)

    contract.phase = 'delivered'
    this.completed++
    this.earnings += payout
    this.lastResult =
      `Delivered — $${payout.toLocaleString()}` +
      (timeFactor < 1 ? ' (late)' : '') +
      (barge.damage > 0.05 ? ' (cargo knocked about)' : '')
    return true
  }

  update(dt: number): void {
    if (this.current?.phase === 'towing') this.current.timeUsed += dt
  }
}
